import React, { useEffect, useState } from 'react';
import { BasketProduct, Product, productsActions as _productsActions } from '../../../store/products';
import {
  Button,
  createDateRange,
  EButtonType,
  EInputType,
  formatDate,
  formatPrice,
  Input,
  KitIcons,
  numberWithSpaces,
  parseToApiDate,
  PopOverlay,
  SpinnerV2,
} from '@repo/apg-ui';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate } from 'react-router';
import { CalendarOverlay } from '../CalendarOverlay';
import { ProductFilter, TimeslotInformation } from '../../../store/products/types';
import { Timeslot, TimeslotOverlay } from '../TimeslotOverlay';
import { DurationOverlay } from '../DurationOverlay';
import { PriceOverlay } from '../PriceOverlay';
import classNames from 'classnames';
import EditModal from '../../Modal/EditModal';
import { FrequencyOverlay } from '../FrequencyOverlay';
import { createSubProductUrn, getPriceForProduct } from '../../../utils/products';
import moment from 'moment/moment';

interface Props {
  productUrn: string;
  product: Product;
  basketProduct?: BasketProduct;
  possibleBookingDates: any;
  productFilterData: ProductFilter;
  setProductFilterData: any;
  getAvailabilityForDigitalProduct: any;
  productFiltersByUrn: IDictionary<ProductFilter>;
  productFiltersDataByUrn: IDictionary<Product>;
  productsByUrn: IDictionary<Product>;
  isLoading: boolean;
  addToBasket: (product: Product, productUrn: string) => void;
  isCalendarLoading: boolean;
  setLoading: (key: string, value: boolean) => void;
  isBatchSelected: (product: Product, string) => boolean;
  batchSelectOrDeselectProduct: (product: Product) => void;
  isAdditionalPeriod?: boolean;
  dateRangeIndex?: number;
  timeslotIndex?: number;
  removeFromBasket: (basketentryid: string, productUrn: string) => void;
  isInBasket: boolean;
  basketEntryId: any;
  setBottomBarFilter: (boolean) => void;
  searchParameters: any;
  productsActions: typeof _productsActions;
  updateBasketProductData: (productUrn, payload, basketEntryId) => void;
}

export const ProductTableRow = ({
  productUrn,
  product,
  possibleBookingDates,
  setProductFilterData,
  productFiltersByUrn,
  productFiltersDataByUrn,
  getAvailabilityForDigitalProduct,
  productsByUrn,
  isLoading,
  addToBasket,
  isInBasket,
  setLoading,
  isCalendarLoading,
  productFilterData,
  isBatchSelected,
  batchSelectOrDeselectProduct,
  isAdditionalPeriod = false,
  dateRangeIndex = 0,
  timeslotIndex = 0,
  removeFromBasket,
  basketEntryId,
  basketProduct,
  setBottomBarFilter,
  searchParameters,
  productsActions,
  updateBasketProductData,
}: Props) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const [selectedTimeslots, setSelectedTimeslots] = useState<number[]>([]);
  const [startDate, setStartDate] = React.useState<string>(product?.startdate || searchParameters?.startDate || null);
  const [dateRanges, setDateRanges] = React.useState([]);
  const [duration, setDuration] = React.useState<number>(product?.pricedays || searchParameters?.duration || 7);
  const [productLoadingStatus, setProductLoadingStatus] = React.useState<boolean>(false);
  const [frequency, setFrequency] = React.useState<string>('1:00');
  const [spotLength, setSpotLength] = React.useState<number>(10);
  const [onEditHover, setOnEditHover] = React.useState<boolean>(false);
  const [onBasketHover, setOnBasketHover] = React.useState<boolean>(false);
  const [showEditModal, setShowEditModal] = React.useState<boolean>(false);
  const sanitizeURN = (urn) => urn?.replace(/[^a-zA-Z0-9-_]/g, '_');

  useEffect(() => {
    let targetStartDate =
      product?.startdate ||
      startDate ||
      (possibleBookingDates && possibleBookingDates.startdigital && possibleBookingDates.startdigital.date) ||
      new Date();

    let productRange = searchParameters?.dateRanges;
    if (!productFiltersByUrn?.[productUrn]?.dateRanges) {
      productRange = createDateRange(new Date(product?.startdate), product?.pricedays, {
        toEndOfYear: false,
        fullWeeks: false,
      });
    }

    const dateRanges = productFiltersByUrn?.[productUrn]?.dateRanges || productRange;

    if (dateRanges && dateRanges.length > 0) {
      targetStartDate = dateRanges[dateRangeIndex].startdate;
      setDuration(productFiltersByUrn[productUrn]?.duration || duration);
      const newDateRanges = dateRanges.map((range) => {
        return {
          range: [
            {
              startDate: new Date(range.startDate),
              endDate: new Date(range.endDate),
              key: 'selection',
              autofocus: true,
              showDateDisplay: true,
            },
          ],
          duration: duration,
          startDate: range.startDate,
          endDate: range.endDate,
        };
      });
      setDateRanges(newDateRanges);
    } else {
      const newDuration = product?.pricedays || duration || searchParameters?.duration || 7;
      const range = createDateRange(new Date(targetStartDate), newDuration, {
        toEndOfYear: false,
        fullWeeks: false,
      });

      setDateRanges([
        {
          range: [range],
          duration: newDuration,
          startDate: range.startDate,
          endDate: range.endDate,
        },
      ]);

      targetStartDate = range.startDate;
      setDuration(newDuration);
    }
    setStartDate(targetStartDate);
  }, [possibleBookingDates, product, productFiltersByUrn]);

  useEffect(() => {
    let initialTimeslots: number[];
    let initialSpotLength: number;

    let initialFrequency: any = product?.frequency || 1;
    if (searchParameters && searchParameters?.timeslot) {
      const newTimeslots = searchParameters?.timeslot;
      initialTimeslots = Array.isArray(newTimeslots) ? newTimeslots[0] : [newTimeslots];
    } else if (product && product?.defaulttimeslot) {
      initialTimeslots = [product.defaulttimeslot];
    } else {
      initialTimeslots = [1];
    }

    initialSpotLength = product?.spotlength || searchParameters?.spotLength || 10;

    if (productUrn && productFiltersByUrn[productUrn]) {
      if (productFiltersByUrn[productUrn]?.timeslots?.length > 0) {
        initialTimeslots = productFiltersByUrn[productUrn].timeslots;
      }
      if (productFiltersByUrn[productUrn]?.spotlength) {
        initialSpotLength = productFiltersByUrn[productUrn].spotlength;
      }
      if (productFiltersByUrn[productUrn]?.frequency) {
        initialFrequency = productFiltersByUrn[productUrn].frequency;
      }
    }

    if (productUrn && productFiltersDataByUrn[productUrn]) {
      const data = productFiltersDataByUrn[productUrn];
      if (productFilterData?.isBudgetChanged) {
        if (data?.frequency) {
          initialFrequency = data.frequency;
        }
        if (data?.frequency) {
          initialFrequency = data.frequency;
        }
      } else {
        if (data?.pricefordates?.length && data?.pricefordates[0]?.frequency) {
          initialFrequency = data?.pricefordates[0]?.frequency;
        }
      }
    }

    setSelectedTimeslots(initialTimeslots);
    setSpotLength(initialSpotLength);
    setFrequency(initialFrequency);
    // setBudget(product?.budget);
  }, [product, product?.defaulttimeslot, product?.spotlength, product?.budget, productFiltersByUrn, searchParameters]);

  const handleDateChange = async (newDateRanges) => {
    document.body.click();
    const data = {
      dateRanges: newDateRanges,
      startdate: parseToApiDate(newDateRanges[0].startDate),
      duration: newDateRanges[0].duration,
      isBudgetChanged: false,
    };

    if (basketEntryId && isInBasket) {
      updateBasketProductData(
        productUrn,
        {
          startdate: moment(data.startdate).format('YYYY-MM-DD'),
          duration: newDateRanges[0].duration,
        },
        basketEntryId,
      );
      setDateRanges(newDateRanges);
      if (newDateRanges.length > 0) {
        setStartDate(newDateRanges[0]?.startDate);
        setDuration(newDateRanges[0]?.duration);
      }

      setProductFilterData(productUrn, data);
    } else {
      updateProductPrice(productUrn, { ...data, spotlength: spotLength });
    }
  };

  const changeDuration = async (value) => {
    document.body.click();
    if (basketEntryId && isInBasket) {
      updateBasketProductData(productUrn, { spotlength: value }, basketEntryId);
      setSpotLength(value);
      setProductFilterData(productUrn, { spotlength: value });
    } else {
      updateProductPrice(productUrn, { spotlength: value });
    }
  };

  const getTotalBroadcasts = () => {
    if (basketEntryId) {
      return numberWithSpaces(basketProduct?.baskettotaltransmission);
    }
    const totaltransmission = productFilterData?.isBudgetChanged
      ? product?.totaltransmission
      : product?.pricefordates?.[0]?.totaltransmission || product?.totaltransmission;

    return numberWithSpaces(totaltransmission);
  };

  const addNewSubRow = (timeslot) => {
    const newDateRanges: any = dateRanges;

    let timeslotData: ProductFilter = productFiltersByUrn[productUrn];
    const subproducturn = createSubProductUrn(product.producturn, timeslot);
    if (!timeslotData || !timeslotData?.subproducturn) {
      timeslotData = {
        subproducturn: subproducturn,
        timeslots: [timeslot],
        duration: duration,
        isBudgetChanged: false,
        isNewTimeslot: true,
        budget: searchParameters?.budget || 0,
        startdate: parseToApiDate(startDate || product?.startdate || searchParameters?.startDate || new Date()),
        frequency: frequency || searchParameters?.frequency || 1,
        spotlength: spotLength || searchParameters?.spotLength || 10,
        dateRanges: newDateRanges,
      };
    }

    setProductFilterData(timeslotData.subproducturn, {
      ...timeslotData,
    });
  };

  const onTimeslotFilterChange = async (option, value) => {
    let newTimeslots;
    if (value) {
      newTimeslots = [...selectedTimeslots, option];
      addNewSubRow(option);
    } else {
      if (selectedTimeslots.length === 1) {
        //to prevent removing the last timeslot
        return;
      }
      newTimeslots = selectedTimeslots.filter((item) => item !== option);

      //remove timeslot from productFiltersByUrn and it's data
      const subproducturn = createSubProductUrn(product.producturn, option);
      setProductFilterData(subproducturn, {});
    }

    if (!isAdditionalPeriod) {
      const subproducturn = createSubProductUrn(product.producturn, newTimeslots[0]);
      const filterData = productFiltersByUrn[subproducturn] || undefined;
      if (filterData && filterData?.subproducturn && newTimeslots.length) {
        delete filterData?.subproducturn;

        setProductFilterData(product.producturn, {
          ...filterData,
          isNewTimeslot: false,
        });

        setProductFilterData(subproducturn, {
          swapAndDelete: true,
          producturn: product.producturn,
        });
      }
    }

    setTimeout(() => {
      let newProductFilterData: any = {
        timeslots: newTimeslots,
      };

      setProductFilterData(product.producturn, newProductFilterData);
      setSelectedTimeslots(newTimeslots);
    }, 0);
  };

  const getSelectedTimeslotName = () => {
    if (!selectedTimeslots.length) return '-';
    const option = product?.context_standard?.timeslotinformation?.find(
      (option) => option.timeslot === selectedTimeslots[timeslotIndex],
    );
    return option ? option.name : '-';
  };

  const handleBudgetChange = async (value) => {
    value = Number(value);
    document.body.click();

    if (basketEntryId && isInBasket) {
      updateBasketProductData(productUrn, { budget: value }, basketEntryId);
      setBudget(value);
      setProductFilterData(productUrn, {
        budget: value,
        isBudgetChanged: true,
      });
    } else {
      calculateShareOfTime(productUrn, { budget: value });
    }
  };

  const handleFrequencyChange = async (value) => {
    document.body.click();

    if (basketEntryId && isInBasket) {
      updateBasketProductData(productUrn, { frequency: value }, basketEntryId);
      setFrequency(value);
      setProductFilterData(productUrn, {
        frequency: value,
        isBudgetChanged: true,
      });
    } else {
      calculateShareOfTime(productUrn, { frequency: value }, true);
    }
  };

  const [forceLoading, setForceLoading] = React.useState<boolean>(false);
  const [budget, setBudget] = React.useState<number>(getPriceForProduct(product));
  const loading = isLoading[productUrn] === true || productLoadingStatus === true;
  const filteredProduct: ProductFilter = productFiltersByUrn[productUrn] || undefined;

  const frequencyOverlayClass = `frequency-overlay-${sanitizeURN(productUrn)}`;
  const priceOverlayClass = `price-overlay-${sanitizeURN(productUrn)}`;

  const handleFrequencyOverlayClick = (e) => {
    e.stopPropagation();
    toggleOverlay(frequencyOverlayClass);

    // Close other overlays within this row
    const rowElement = e.currentTarget.closest('.product-table__row');
    const otherOverlays = rowElement.querySelectorAll('.pop-overlay');
    otherOverlays.forEach((overlay) => {
      if ((overlay as HTMLElement).id !== frequencyOverlayClass) {
        (overlay as HTMLElement).style.display = 'none';
      }
    });
  };

  const toggleOverlay = (frequencyOverlayClass) => {
    const overlay = document.querySelector(`.${frequencyOverlayClass}`) as HTMLElement;
    if (overlay) {
      return;
    } else {
      document.body.click(); // Close other overlays
    }
  };

  useEffect(() => {
    setBudget(filteredProduct?.budget || getPriceForProduct(product));
    setSpotLength(filteredProduct?.spotlength || product?.spotlength || searchParameters?.spotLength || 10);
    setDuration(filteredProduct?.duration || product?.pricedays || searchParameters?.duration || 7);
    setFrequency(filteredProduct?.frequency || product?.frequency || '1:00');
    setStartDate(filteredProduct?.startdate || product?.startdate || searchParameters?.startDate);
  }, []);

  useEffect(() => {
    if (productFilterData?.isNewTimeslot) {
      setForceLoading(true);
      updateProductPrice(productUrn);
      setForceLoading(false);
    }
  }, [productFilterData?.isNewTimeslot]);

  if (!product) return null;

  const filterTimeslotOptions = (timeslotArray: TimeslotInformation[], keys: number[]): Timeslot[] => {
    if (!timeslotArray) return [];
    return timeslotArray
      .filter((timeslot) => keys?.includes(timeslot.timeslot))
      .map((timeslot) => {
        return {
          ...timeslot,
          title: timeslot.name,
          value: timeslot.timespan,
          timeslot: timeslot.timeslot,
        };
      });
  };

  const getSpotLength = () => {
    if (basketEntryId) {
      return basketProduct?.basketspotlength;
    }
    return spotLength;
  };

  const getFrequency = () => {
    if (basketEntryId) {
      return basketProduct?.basketfrequency;
    }
    return frequency;
  };

  const getDisplayBudgetValue = () => {
    if (basketEntryId) {
      return formatPrice(basketProduct?.basketprice);
    }

    return productFilterData?.isBudgetChanged
      ? formatPrice(product?.budget || budget)
      : formatPrice(getPriceForProduct(product));
  };

  const getInputFieldBudgetValue = () => {
    if (basketEntryId) {
      return basketProduct?.basketprice;
    }

    return productFilterData?.isBudgetChanged
      ? product?.budget
        ? product?.budget
        : budget
      : getPriceForProduct(product);
  };

  const getPayloadForProductUpdates = () => {
    let payload: any = {
      producturn: productUrn,
      duration: duration || product?.pricedays || searchParameters.duration,
      startdate: parseToApiDate(startDate || product?.startdate || searchParameters.startDate),
      spotlength: spotLength || product?.spotlength || searchParameters.spotLength,
      timeslot: searchParameters.timeslot,
    };

    const productFilter = productFiltersByUrn[productUrn];

    if (productFilter) {
      const productFiltersKeys = Object.keys(productFilter);

      if (productFiltersKeys.includes('spotlength')) {
        payload.spotlength = productFilter.spotlength;
      }

      if (productFiltersKeys.includes('startdate')) {
        payload.startdate = parseToApiDate(productFilter.startdate);
      }

      if (productFiltersKeys.includes('duration')) {
        payload.duration = productFilter.duration;
      }

      if (productFiltersKeys.includes('timeslots')) {
        payload.timeslot = productFilter.timeslots[0];
      }
    }

    return payload;
  };

  const calculateShareOfTime = (productUrn, updatedValues = undefined, isFrequency = false) => {
    setProductLoadingStatus(true);
    let payload: any = getPayloadForProductUpdates();

    if (updatedValues) {
      const updatedValuesKeys = Object.keys(updatedValues);

      if (updatedValuesKeys.includes('budget')) {
        payload.budget = updatedValues.budget;
      }

      if (updatedValuesKeys.includes('frequency')) {
        payload.frequency = updatedValues.frequency;
      }
    }
    productsActions
      .calculateShareOfTime(payload)
      .then((result) => {
        setProductLoadingStatus(false);

        if (result && result?.data?.producturn) {
          productsActions.setProductFilterData(productUrn, {
            budget: result?.data?.budget,
            frequency: result?.data?.frequency,
            isBudgetChanged: true,
          });

          setBudget(result?.data?.frequency);
          setFrequency(result?.data?.budget);
        }
      })
      .catch((error) => {
        console.error('Error calculation share of time:', error);
        setProductLoadingStatus(false);
      });
  };

  const updateProductPrice = (productUrn, updatedValues = undefined) => {
    setProductLoadingStatus(true);
    let payload: any = getPayloadForProductUpdates();

    if (updatedValues) {
      const updatedValuesKeys = Object.keys(updatedValues);

      if (updatedValuesKeys.includes('spotlength')) {
        payload.spotlength = updatedValues.spotlength;
      }

      if (updatedValuesKeys.includes('startdate')) {
        payload.startdate = updatedValues.startdate;
      }

      if (updatedValuesKeys.includes('duration')) {
        payload.duration = updatedValues.duration;
      }
    }

    productsActions
      .getPriceForProduct(payload)
      .then((result) => {
        setProductLoadingStatus(false);

        if (result && result?.data?.producturn) {
          const resultData = result.data;
          const resultPriceData = resultData?.prices?.[0] || undefined;
          let data: any = {
            startdate: moment(resultData.startdate).format('YYYY-MM-DD'),
            enddate: moment(resultData.enddate).format('YYYY-MM-DD'),
          };

          let newFilterData = {
            ...updatedValues,
          };

          if (resultPriceData) {
            data.budget = resultPriceData.price;
            data.spotlength = resultPriceData.spotlength;
            data.startdate = moment(resultPriceData.startdate).format('YYYY-MM-DD');
            data.duration = resultPriceData.days;

            const range = createDateRange(new Date(data.startdate), data.duration, {
              toEndOfYear: false,
              fullWeeks: false,
            });

            newFilterData = {
              ...newFilterData,
              ...data,
              dateRanges: [
                {
                  range: [range],
                  duration: data.duration,
                  startDate: range.startDate,
                  endDate: range.endDate,
                },
              ],
            };
          }

          setSpotLength(newFilterData.spotlength);
          setDateRanges(newFilterData.dateRanges);
          if (newFilterData?.dateRanges.length > 0) {
            setStartDate(newFilterData?.dateRanges[0]?.startDate);
            setDuration(newFilterData?.dateRanges[0]?.duration);
          }

          productsActions.setProductFilterData(productUrn, {
            ...newFilterData,
            budget: undefined,
            isBudgetChanged: false,
            isNewTimeslot: false,
          });
        }
      })
      .catch((error) => {
        console.error('Error price calculation:', error);
        setProductLoadingStatus(false);
      });
  };

  return (
    <div
      className={classNames('relative d-flex ', {
        'bg-gray-lighter': isInBasket,
      })}
    >
      {loading === true && (
        <div className='product-loader'>
          <SpinnerV2 relative />
        </div>
      )}
      <div
        className={classNames('product-table__row table-row-top', {
          disabled: loading === true,
        })}
      >
        <div className='product-table__row__cell product-title'>
          {!isAdditionalPeriod && (
            <div className='w-100 d-flex align-items-center product-batch-gap'>
              <Input
                inputType={EInputType.Checkbox}
                type={'checkbox'}
                inputWrapperClass={'checkbox-input-wrap'}
                name={`group_product_${productUrn}_${product?.timeslot}`}
                checked={isBatchSelected(product, 'product')}
                onChange={() => batchSelectOrDeselectProduct(product)}
              />
              <div className='d-flex flex-column font-bold'>
                <Button
                  buttonType={EButtonType.BasicLink}
                  className='product-name-link'
                  onClick={() =>
                    navigate({
                      search: `?modalContent=product&urn=${productUrn}`,
                    })
                  }
                >
                  {product?.name}
                </Button>
                <span className='cell-text'>
                  {product.mediacount} <FormattedMessage id='ec.product.screen' defaultMessage='Screen(s)' /> - (
                  {product?.additionalinfo?.animation})
                </span>
              </div>
            </div>
          )}
        </div>

        <PopOverlay
          className='product-table__row__cell dates'
          content={
            dateRanges.length > 0 && (
              <CalendarOverlay
                possibleBookingDates={possibleBookingDates}
                dateRanges={[dateRanges[0]]}
                tags={product?.tags}
                isDigital={product.producttype === 'digital'}
                handleDateChange={handleDateChange}
                producturn={product.producturn}
                spotlength={filteredProduct?.spotlength || product.spotlength}
                timeslots={filteredProduct?.timeslots || [product.defaulttimeslot]}
                productAvailability={productsByUrn[product.producturn]?.availabilities}
                getAvailabilityForDigitalProduct={getAvailabilityForDigitalProduct}
                setLoading={setLoading}
                isLoading={isCalendarLoading}
                isDisabled={forceLoading}
              />
            )
          }
        >
          <>
            <span className='cell-header'>
              {formatDate(dateRanges[dateRangeIndex]?.startDate, 'dd.MM.yyyy', intl.locale)} -{' '}
              {formatDate(dateRanges[dateRangeIndex]?.endDate, 'dd.MM.yyyy', intl.locale)}
            </span>
            <span className='cell-text'>
              <span>
                {duration || ''} <FormattedMessage id='ec.checkout.days' defaultMessage='Tag(e)' />
              </span>
              {!forceLoading && (
                <span className='edit-icon'>
                  <KitIcons.EditOutlineIcon />
                </span>
              )}
            </span>
          </>
        </PopOverlay>

        {isAdditionalPeriod ? (
          <div className='product-table__row__cell timeslot'>
            <>
              <span className='cell-header'>
                <FormattedMessage id='ec.checkout.timeslot' defaultMessage='Zeitfenster' />
              </span>
              <span className='cell-text'>
                <span>{getSelectedTimeslotName()}</span>
              </span>
            </>
          </div>
        ) : (
          <PopOverlay
            className='product-table__row__cell timeslot'
            content={
              <TimeslotOverlay
                options={filterTimeslotOptions(
                  product?.context_standard?.timeslotinformation,
                  product?.context_standard?.timeslots,
                )}
                selectedTimeslot={selectedTimeslots}
                onSelectionChange={onTimeslotFilterChange}
                isDisabled={forceLoading}
                allowedTimeslots={[1, 11, 12, 13, 14]}
              />
            }
          >
            <>
              <span className='cell-header'>
                <FormattedMessage id='ec.checkout.timeslot' defaultMessage='Zeitfenster' />
              </span>
              <span className='cell-text'>
                <span>{getSelectedTimeslotName()}</span>
                {!forceLoading && (
                  <span className='edit-icon'>
                    <KitIcons.EditOutlineIcon />
                  </span>
                )}
              </span>
            </>
          </PopOverlay>
        )}

        <PopOverlay
          className='product-table__row__cell duration'
          content={
            <DurationOverlay
              durationLabels={intl.formatMessage({
                id: 'ec.checkout.sekunden',
                defaultMessage: 'Sekunden',
              })}
              placeholderText={intl.formatMessage({
                id: 'ec.checkout.sekunden',
                defaultMessage: 'Sekunden',
              })}
              inputValue={getSpotLength}
              onChange={changeDuration}
              isDisabled={forceLoading}
            />
          }
        >
          <>
            <span className='cell-header'>
              <FormattedMessage id={'home.button.duration'} defaultMessage={'Spotlänge'} />
            </span>
            <span className='cell-text'>
              <span>
                {getSpotLength()} <FormattedMessage id={'home.button.sekunden'} defaultMessage={'Sekunden'} />
              </span>
              {!forceLoading && (
                <span className='edit-icon'>
                  <KitIcons.EditOutlineIcon />
                </span>
              )}
            </span>
            <span className='cell-header'>
              <FormattedMessage
                id='ec.modals.resultList.columnTitles.totalBroadcasts'
                defaultMessage='Total Ausstrahlungen'
              />
            </span>
            <span className='cell-text'>
              <span>{getTotalBroadcasts()}</span>
            </span>
          </>
        </PopOverlay>
        <div className='product-table__row__cell price'>
          <PopOverlay
            className={`price-block ${priceOverlayClass}`}
            content={
              <PriceOverlay
                priceLabels={intl.formatMessage({
                  id: 'ec.modals.resultList.columnTitles.price',
                  defaultMessage: 'Price',
                })}
                placeholderText={intl.formatMessage({
                  id: 'ec.modals.resultList.columnTitles.price',
                  defaultMessage: 'Price',
                })}
                inputValue={getInputFieldBudgetValue()}
                onChange={(value) => handleBudgetChange(value)}
                isDisabled={forceLoading}
              />
            }
          >
            <>
              {forceLoading ? (
                <SpinnerV2 relative />
              ) : (
                <>
                  <span className='cell-header'>
                    <FormattedMessage id='ec.modals.resultList.columnTitles.price' defaultMessage='Preis' />
                  </span>
                  <span className='cell-text'>
                    {getDisplayBudgetValue()}
                    {' CHF'}
                    {!forceLoading && (
                      <span className='edit-icon'>
                        <KitIcons.EditOutlineIcon />
                      </span>
                    )}
                  </span>
                </>
              )}
            </>
          </PopOverlay>

          <PopOverlay
            className={`frequency-block ${frequencyOverlayClass}`}
            content={
              <FrequencyOverlay
                frequencyLabels={intl.formatMessage({
                  id: 'ec.checkout.frequency',
                  defaultMessage: 'Frequenz',
                })}
                placeholderText={intl.formatMessage({
                  id: 'ec.checkout.frequency',
                  defaultMessage: 'Frequenz',
                })}
                inputValue={getFrequency()}
                onChange={(value) => handleFrequencyChange(value)}
                extraClassName={frequencyOverlayClass}
                isDisabled={forceLoading}
              />
            }
          >
            <>
              {forceLoading ? (
                <SpinnerV2 relative />
              ) : (
                <>
                  <span className='cell-header'>
                    <FormattedMessage id='ec.modals.resultList.columnTitles.frequenz' defaultMessage='Frequenz' />
                  </span>
                  <span className='cell-text'>
                    {getFrequency()}{' '}
                    <FormattedMessage id='ec.modals.resultList.columnTitles.frequenz.minutes' defaultMessage='Min.' />
                    {!forceLoading && (
                      <span className='edit-icon'>
                        <KitIcons.EditOutlineIcon />
                      </span>
                    )}
                  </span>
                </>
              )}
            </>
          </PopOverlay>
        </div>

        <div className='product-table__row__cell actions'>
          {/*<div
                        className="edit"
                        onMouseEnter={() => setOnEditHover(true)}
                        onMouseLeave={() => setOnEditHover(false)}
                    >
                        <Button
                            buttonType={EButtonType.InvertedPillMedium}
                            onClick={() => setShowEditModal(true)}
                        >
                            <div className="button-svg">
                                {onEditHover ? (
                                    <>
                                        <KitIcons.WhiteEditOutlineIcon /> Edit
                                    </>
                                ) : (
                                    <KitIcons.EditOutlineIcon />
                                )}
                            </div>
                        </Button>
                    </div>*/}
          <div
            className={`${!isInBasket ? 'add-basket' : 'in-basket'}`}
            onMouseEnter={() => !isInBasket && setOnBasketHover(true)}
            onMouseLeave={() => !isInBasket && setOnBasketHover(false)}
          >
            <Button
              buttonType={EButtonType.InvertedPillMedium}
              onClick={() => {
                if (isInBasket) {
                  removeFromBasket(basketEntryId, productUrn);
                } else {
                  addToBasket(product, productUrn);
                }
              }}
            >
              <div className='button-svg'>
                {isInBasket ? (
                  <KitIcons.WhiteTrashIcon />
                ) : onBasketHover ? (
                  <>
                    <KitIcons.WhitePlusIcon /> <FormattedMessage id='batchSelect.add' defaultMessage='Hinzufügen' />
                  </>
                ) : (
                  <KitIcons.PlusIcon />
                )}
              </div>
            </Button>
          </div>
        </div>
      </div>
      {showEditModal && (
        <EditModal
          productUrn={productUrn}
          isAdditionalPeriod={isAdditionalPeriod}
          show={showEditModal}
          setHide={setShowEditModal}
          setProductFilterData={setProductFilterData}
          selectedProducts={{ ...product }}
        />
      )}
    </div>
  );
};
